export const MAX_LAUNCHER_POSITION = 255;

export type LauncherPositions = Record<string, number>;

type PositionedApplication = {
  appCode: string;
};

type LauncherPositionItem = {
  appCode: string;
  position: number;
};

export function launcherPositionsFromItems(
  items: readonly LauncherPositionItem[],
): LauncherPositions {
  return Object.fromEntries(items.map(({ appCode, position }) => [appCode, position]));
}

export function launcherPositionItemsFromPositions(
  positions: LauncherPositions,
): LauncherPositionItem[] {
  return Object.entries(positions)
    .map(([appCode, position]) => ({ appCode, position }))
    .sort((left, right) => left.position - right.position || left.appCode.localeCompare(right.appCode));
}

export function resolveLauncherPositions<TApplication extends PositionedApplication>(
  applications: readonly TApplication[],
  savedPositions: LauncherPositions,
): LauncherPositions {
  const positions = validUniquePositions(savedPositions);
  const occupied = new Set(Object.values(positions));
  let candidate = 0;

  applications.forEach((application) => {
    if (positions[application.appCode] !== undefined) {
      return;
    }

    while (occupied.has(candidate)) {
      candidate += 1;
    }

    positions[application.appCode] = candidate;
    occupied.add(candidate);
  });

  return positions;
}

export function sortLauncherApplications<TApplication extends PositionedApplication>(
  applications: readonly TApplication[],
  positions: LauncherPositions,
): TApplication[] {
  return applications
    .map((application, defaultIndex) => ({
      application,
      defaultIndex,
      position: positions[application.appCode] ?? Number.MAX_SAFE_INTEGER,
    }))
    .sort((left, right) => (
      left.position - right.position
      || left.defaultIndex - right.defaultIndex
    ))
    .map(({ application }) => application);
}

export function centerLauncherPositions<TApplication extends PositionedApplication>(
  applications: readonly TApplication[],
  columnCount: number,
  rowCount: number,
): LauncherPositions {
  if (applications.length === 0) {
    return {};
  }

  const safeColumnCount = Math.max(1, Math.floor(columnCount));
  const applicationRowCount = Math.ceil(applications.length / safeColumnCount);
  const safeRowCount = Math.max(applicationRowCount, Math.floor(rowCount));
  const firstApplicationRow = Math.floor((safeRowCount - applicationRowCount) / 2);
  const firstPosition = firstApplicationRow * safeColumnCount;

  return Object.fromEntries(applications.map((application, index) => [
    application.appCode,
    firstPosition + index,
  ]));
}

export function fitLauncherPositionsToGrid<TApplication extends PositionedApplication>(
  applications: readonly TApplication[],
  positions: LauncherPositions,
  slotCount: number,
): LauncherPositions {
  const safeSlotCount = Math.max(applications.length, Math.floor(slotCount));
  const fitted: LauncherPositions = {};
  const occupied = new Set<number>();

  applications.forEach((application) => {
    const position = positions[application.appCode];

    if (
      Number.isInteger(position)
      && position >= 0
      && position < safeSlotCount
      && !occupied.has(position)
    ) {
      fitted[application.appCode] = position;
      occupied.add(position);
    }
  });

  let candidate = 0;

  applications.forEach((application) => {
    if (fitted[application.appCode] !== undefined) {
      return;
    }

    while (occupied.has(candidate)) {
      candidate += 1;
    }

    fitted[application.appCode] = candidate;
    occupied.add(candidate);
  });

  return fitted;
}

export function moveLauncherApplication(
  positions: LauncherPositions,
  appCode: string,
  targetPosition: number,
): LauncherPositions {
  const sourcePosition = positions[appCode];

  if (
    sourcePosition === undefined
    || sourcePosition === targetPosition
    || !Number.isInteger(targetPosition)
    || targetPosition < 0
    || targetPosition > MAX_LAUNCHER_POSITION
  ) {
    return positions;
  }

  const targetEntry = Object.entries(positions).find(
    ([candidateAppCode, position]) => (
      candidateAppCode !== appCode && position === targetPosition
    ),
  );

  if (!targetEntry) {
    return {
      ...positions,
      [appCode]: targetPosition,
    };
  }

  const occupiedPositions = Object.values(positions).sort((left, right) => left - right);
  const orderedAppCodes = Object.entries(positions)
    .sort((left, right) => left[1] - right[1])
    .map(([candidateAppCode]) => candidateAppCode);
  const sourceIndex = orderedAppCodes.indexOf(appCode);
  const targetIndex = orderedAppCodes.indexOf(targetEntry[0]);
  const reorderedAppCodes = [...orderedAppCodes];

  reorderedAppCodes.splice(sourceIndex, 1);
  reorderedAppCodes.splice(targetIndex, 0, appCode);

  return Object.fromEntries(reorderedAppCodes.map((candidateAppCode, index) => [
    candidateAppCode,
    occupiedPositions[index],
  ]));
}

export function launcherGridRowCount(
  applicationCount: number,
  columnCount: number,
  availableHeight: number,
): number {
  const cellHeight = 144;
  const rowGap = 20;
  const safeColumnCount = Math.max(1, Math.floor(columnCount));
  const applicationRowCount = Math.max(1, Math.ceil(applicationCount / safeColumnCount));
  return Math.max(
    applicationRowCount,
    Math.floor((Math.max(0, availableHeight) + rowGap) / (cellHeight + rowGap)),
  );
}

export function launcherGridColumnCount(viewportWidth: number): number {
  if (viewportWidth >= 1536) {
    return 6;
  }

  if (viewportWidth >= 1280) {
    return 5;
  }

  if (viewportWidth >= 1024) {
    return 4;
  }

  if (viewportWidth >= 640) {
    return 3;
  }

  return 2;
}

function validUniquePositions(positions: LauncherPositions): LauncherPositions {
  const valid: LauncherPositions = {};
  const occupied = new Set<number>();

  Object.entries(positions).forEach(([appCode, position]) => {
    if (
      !Number.isInteger(position)
      || position < 0
      || position > MAX_LAUNCHER_POSITION
      || occupied.has(position)
    ) {
      return;
    }

    valid[appCode] = position;
    occupied.add(position);
  });

  return valid;
}
